/**
 * EVIDENCE — every record enclosed in this work, and what each one turned out
 * to be.
 *
 * PROVENANCE IS THE POINT, not a footnote. Every figure in the file rests on
 * one of these, so each record is shown with what it is, who enclosed it,
 * when, the digest of the exact bytes that were read, and which reader read
 * them. Someone reproducing this work in a year needs all of it.
 *
 * A REFUSED RECORD STAYS. It is not hidden and it is not retried away: the act
 * of enclosing it happened, the reader gave a reason, and both are part of the
 * professional history of the work. The reason shown is the reader's own.
 *
 * Leveri holds each record as an immutable version and reads from that version
 * only — which is why the identifiers of those versions live here, behind a
 * disclosure, rather than on the reading surface.
 */
import type { EnclosedRecord, WorkFile } from "@leveri/contracts";
import {
  Citation, Definitions, FileTitle, Notice, RecordName, RuledList, Section, SectionHead,
} from "../../file/index.js";
import { EncloseRecord } from "./enclose.js";

export function EvidenceSection({ work, onWork }: { work: WorkFile; onWork: (work: WorkFile) => void }) {
  const admitted = work.records.filter((record) => record.outcome === "ADMITTED");
  const refused = work.records.filter((record) => record.outcome === "REFUSED");

  return (
    <div>
      <FileTitle
        kicker="Evidence"
        size={28}
        meta="Every figure in this file rests on one of these. Leveri reads records; it never edits them."
      >
        {work.records.length === 0 ? "No records yet" : "The records of this work"}
      </FileTitle>

      <Section>
        <SectionHead>Enclose a record</SectionHead>
        <EncloseRecord workId={work.id} kinds={work.start.kinds} onEnclosed={onWork} />
      </Section>

      <Section>
        <SectionHead aside={admitted.length > 0 ? String(admitted.length) : null}>Admitted</SectionHead>
        {admitted.length === 0 ? (
          <p className="text-sm text-stone">No record has been admitted to this work yet.</p>
        ) : (
          <RuledList>{admitted.map((record) => <Record key={record.enclosureId} record={record} />)}</RuledList>
        )}
      </Section>

      {refused.length > 0 && (
        <Section>
          <SectionHead aside={String(refused.length)}>Could not be used</SectionHead>
          <RuledList>
            {refused.map((record) => (
              <div key={record.enclosureId} className="py-3.5">
                <RecordName className="block truncate">{record.filename}</RecordName>
                {/* The server's plain reading of the refusal; the reason as recorded stays one hover away. */}
                <div className="mt-1.5" title={record.refusalReason ?? undefined}><Notice>{record.refusalPlain ?? record.refusalReason ?? "It could not be used."}</Notice></div>
                <div className="mt-1.5" title={record.enclosedAt}>
                  <Citation>{record.enclosedBy} · {record.enclosedAtText}</Citation>
                </div>
              </div>
            ))}
          </RuledList>
        </Section>
      )}

      {work.records.length > 0 && (
        <>
          <p className="mt-7 text-[13px] text-stone">
            Leveri holds each record as an immutable version and reads from that version only. The original file is not
            retrievable from Leveri; keep the client&rsquo;s own copy alongside the workpaper.
          </p>
          <details className="mt-4">
            <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">Technical provenance</summary>
            <RuledList className="mt-2 border-y border-rule">
              {work.records.map((record) => (
                <div key={`provenance-${record.enclosureId}`} className="py-2.5">
                  <RecordName className="block truncate">{record.filename}</RecordName>
                  <Citation as="div" className="mt-1 break-all">Enclosure {record.enclosureId}</Citation>
                  <Citation as="div" className="break-all">Source version {record.sourceVersionId ?? "—"}</Citation>
                  <Citation as="div" className="break-all">Evidence reference {record.evidenceReferenceId ?? "—"}</Citation>
                  <Citation as="div" className="break-all">Normaliser {record.normalizer ?? "—"}</Citation>
                </div>
              ))}
            </RuledList>
          </details>
        </>
      )}
    </div>
  );
}

/**
 * One admitted record. Only the facts the projection actually carries are
 * shown: a null reader, digest or role means the record does not have one, and
 * an empty row is more honest than a dash pretending to be a value.
 */
function Record({ record }: { record: EnclosedRecord }) {
  const items = [
    record.roleLabel ? { key: "role", term: "Read as", value: record.roleLabel } : null,
    {
      key: "by",
      term: "Enclosed by",
      value: record.enclosedBy,
      // The reading a person sees; the machine value stays in the title for
      // anyone reproducing the work, and on the wire either way.
      citation: <span title={record.enclosedAt}><Citation>{record.enclosedAtText}</Citation></span>,
    },
    { key: "size", term: "Size", value: <span className="[font-variant-numeric:tabular-nums]">{record.byteSize} bytes</span> },
    record.reader ? { key: "reader", term: "Read by", value: record.reader } : null,
    record.sha256 ? { key: "digest", term: "Digest", value: <Citation className="break-all">{record.sha256}</Citation> } : null,
    record.sheets.length > 0
      ? {
          key: "sheets",
          term: record.sheets.length === 1 ? "Sheet" : "Sheets",
          value: (
            <span className="[font-variant-numeric:tabular-nums]">
              {record.sheets.map((sheet) => `${sheet.name} — ${sheet.rowCount} rows`).join(" · ")}
            </span>
          ),
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <div className="py-3.5">
      <RecordName className="block truncate">{record.filename}</RecordName>
      <Definitions compact className="mt-2" items={items} />
    </div>
  );
}
